"use client"
import { useState, useEffect } from "react"
import axios from "axios"
import { ArrowUpward, ArrowDownward, Edit, Delete } from "@mui/icons-material"
import {
  Box,
  Card,
  CardContent,
  Button,
  Typography,
  Grid,
  FormControl,
  Select,
  MenuItem,
  TextField,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  CircularProgress,
  Alert,
} from "@mui/material"
import AddNsmModal from "../../components/Nsm/AddNsmModal" 
import EditNsmModal from "../../components/Nsm/EditNsmModal" 
import AddNsmGroupModal from "../../components/Nsm/AddNsmGroupModal" 
import ConfirmationDialog from "../../components/Common/ConfirmationDialog"
import { useOutletContext } from "react-router-dom"
import BaseURL from "../../api/AppURL";

const NationalSalesManager = () => {
  const [nationalSalesManagers, setNationalSalesManagers] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [searchTerm, setSearchTerm] = useState("")
  const [entriesPerPage, setEntriesPerPage] = useState(25)
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "ascending" })
  const [downloading, setDownloading] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)

  // Modal states
  const [addModalOpen, setAddModalOpen] = useState(false)
  const [editModalOpen, setEditModalOpen] = useState(false)
  const [currentNationalSalesManagerId, setCurrentNationalSalesManagerId] = useState(null)
  const [addNationalSalesManagerGroupModalOpen, setAddNationalSalesManagerGroupModalOpen] = useState(false)

  // Confirmation Dialog states
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false)
  const [nationalSalesManagerToDeleteId, setNationalSalesManagerToDeleteId] = useState(null)

  const { showSnackbar } = useOutletContext()

  const fetchNationalSalesManagers = async () => {
    setLoading(true)
    setError(null)
    try {
      const token = localStorage.getItem("auth_token")
      // আপাতত সাপ্লায়ার API ব্যবহার করা হচ্ছে
      const response = await axios.get(`${BaseURL}/all/nsmanager`, {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
      })
      const fetchedData = Array.isArray(response.data.nsnanager) ? response.data.nsnanager : []
      setNationalSalesManagers(fetchedData)
      console.log("National Sales Managers (using supplier API) fetched successfully. Total items:", fetchedData.length)
    } catch (err) {
      console.error("Error fetching National Sales Managers (using supplier API):", err)
      setError("Failed to load National Sales Managers. Please try again later.")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchNationalSalesManagers()
  }, [])

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value)
    setCurrentPage(1)
  }

  const handleEntriesChange = (event) => {
    setEntriesPerPage(event.target.value)
    setCurrentPage(1)
  }

  const requestSort = (key) => {
    let direction = "ascending"
    if (sortConfig.key === key && sortConfig.direction === "ascending") {
      direction = "descending"
    }
    setSortConfig({ key, direction })
  }

  const sortedNationalSalesManagers = [...nationalSalesManagers].sort((a, b) => {
    if (sortConfig.key === null) return 0

    let aValue = a[sortConfig.key]
    let bValue = b[sortConfig.key]

    if (sortConfig.key === "group") {
      aValue = a.group?.name || ""
      bValue = b.group?.name || ""
    }

    if (aValue < bValue) {
      return sortConfig.direction === "ascending" ? -1 : 1
    }
    if (aValue > bValue) {
      return sortConfig.direction === "ascending" ? 1 : -1
    }
    return 0
  })

  const filteredNationalSalesManagers = sortedNationalSalesManagers.filter((manager) => {
    const searchLower = searchTerm.toLowerCase()
    return (
      String(manager.name).toLowerCase().includes(searchLower) ||
      String(manager.mobile_no).toLowerCase().includes(searchLower) ||
      String(manager.address).toLowerCase().includes(searchLower) ||
      String(manager.group?.name || "")
        .toLowerCase()
        .includes(searchLower)
    )
  })

  const indexOfLastNationalSalesManager = currentPage * entriesPerPage
  const indexOfFirstNationalSalesManager = indexOfLastNationalSalesManager - entriesPerPage
  const displayedNationalSalesManagers = filteredNationalSalesManagers.slice(
    indexOfFirstNationalSalesManager,
    indexOfLastNationalSalesManager,
  )
  const totalPages = Math.ceil(filteredNationalSalesManagers.length / entriesPerPage)

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return null
    return sortConfig.direction === "ascending" ? <ArrowUpward fontSize="small" /> : <ArrowDownward fontSize="small" />
  }

  const handleNextPage = () => {
    setCurrentPage((prevPage) => Math.min(prevPage + 1, totalPages))
  }

  const handlePreviousPage = () => {
    setCurrentPage((prevPage) => Math.max(prevPage - 1, 1))
  }

  const totalEntries = filteredNationalSalesManagers.length
  const startEntry = totalEntries === 0 ? 0 : indexOfFirstNationalSalesManager + 1
  const endEntry = Math.min(indexOfLastNationalSalesManager, totalEntries)

  const handleDownloadPdf = async () => {
    setDownloading(true)
    try {
      const token = localStorage.getItem("auth_token")
      // আপাতত সাপ্লায়ার API ব্যবহার করা হচ্ছে
      const response = await axios.get(`${BaseURL}/all/nsmanager/report`, {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/pdf",
        },
        responseType: "blob",
      })

      const contentDisposition = response.headers["content-disposition"]
      console.log("Content-Disposition Header:", contentDisposition)
      let filename = "national_sales_managers_report.pdf"
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="([^"]+)"/)
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1]
        } else {
          const unquotedFilenameMatch = contentDisposition.match(/filename=([^;]+)/)
          if (unquotedFilenameMatch && unquotedFilenameMatch[1]) {
            filename = unquotedFilenameMatch[1].trim()
          }
        }
      }

      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement("a")
      link.href = url
      link.setAttribute("download", filename)
      document.body.appendChild(link)
      link.click()
      link.parentNode.removeChild(link)
      window.URL.revokeObjectURL(url)
      showSnackbar("PDF report downloaded successfully!", "success")
    } catch (err) {
      console.error("Error downloading PDF:", err)
      showSnackbar("Failed to download PDF report. Please try again.", "error")
    } finally {
      setDownloading(false)
    }
  }

  const renderPageNumbers = () => {
    const pageNumbers = []
    for (let i = 1; i <= totalPages; i++) {
      pageNumbers.push(
        <Button
          key={i}
          variant={currentPage === i ? "contained" : "outlined"}
          color={currentPage === i ? "primary" : "inherit"}
          onClick={() => setCurrentPage(i)}
          sx={{ minWidth: 40, px: 1 }}
        >
          {i}
        </Button>,
      )
    }
    return pageNumbers
  }

  const handleAddNationalSalesManagerClick = () => {
    setAddModalOpen(true)
  }

  const handleAddModalClose = () => {
    setAddModalOpen(false)
  }

  const handleNationalSalesManagerAddedSuccess = (message) => {
    showSnackbar(message, "success")
    fetchNationalSalesManagers()
  }

  const handleEditNationalSalesManagerClick = (managerId) => {
    setCurrentNationalSalesManagerId(managerId)
    setEditModalOpen(true)
  }

  const handleEditModalClose = () => {
    setEditModalOpen(false)
    setCurrentNationalSalesManagerId(null)
  }

  const handleNationalSalesManagerUpdatedSuccess = (message) => {
    showSnackbar(message, "success")
    fetchNationalSalesManagers()
  }

  const handleDeleteNationalSalesManagerClick = (managerId) => {
    setNationalSalesManagerToDeleteId(managerId)
    setConfirmDialogOpen(true)
  }

  const handleConfirmDialogClose = () => {
    setConfirmDialogOpen(false)
    setNationalSalesManagerToDeleteId(null)
  }

  const handleConfirmNationalSalesManagerDelete = async () => {
    if (!nationalSalesManagerToDeleteId) return

    setConfirmDialogOpen(false)
    console.log("Attempting to delete National Sales Manager with ID:", nationalSalesManagerToDeleteId)

    try {
      const token = localStorage.getItem("auth_token")
      // আপাতত সাপ্লায়ার API ব্যবহার করা হচ্ছে
      const response = await axios.delete(
        `${BaseURL}/delete/nsmanager/${nationalSalesManagerToDeleteId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "application/json",
          },
        },
      )

      console.log("DELETE API Response:", response.data)

      const isSuccess = response.data.success === true || response.data.message?.includes("successfully")

      if (isSuccess) {
        showSnackbar(response.data.message || "National Sales Manager deleted successfully!", "success")
        console.log("National Sales Manager deleted successfully. Optimistically updating UI...")

        setNationalSalesManagers((prevManagers) => {
          const updatedManagers = prevManagers.filter((m) => {
            const isMatch = String(m.id) === String(nationalSalesManagerToDeleteId)
            console.log(
              `Comparing m.id: ${m.id} (type: ${typeof m.id}) with nationalSalesManagerToDeleteId: ${nationalSalesManagerToDeleteId} (type: ${typeof nationalSalesManagerToDeleteId}). Match: ${isMatch}`,
            )
            return !isMatch
          })
          console.log("National Sales Managers after optimistic update. New count:", updatedManagers.length)
          return updatedManagers
        })

        await fetchNationalSalesManagers()
        console.log("Table re-synced with backend after deletion.")
      } else {
        showSnackbar(response.data.message || "Failed to delete National Sales Manager.", "error")
        console.log(
          "Failed to delete National Sales Manager (API reported failure or unexpected response):",
          response.data.message,
        )
      }
    } catch (err) {
      console.error("Error deleting National Sales Manager:", err)
      showSnackbar("An error occurred while deleting the National Sales Manager.", "error")
    } finally {
      setNationalSalesManagerToDeleteId(null)
    }
  }

  const handleAddNationalSalesManagerGroupClick = () => {
    setAddNationalSalesManagerGroupModalOpen(true)
  }

  const handleAddNationalSalesManagerGroupModalClose = () => {
    setAddNationalSalesManagerGroupModalOpen(false)
  }

  const handleNationalSalesManagerGroupAddedSuccess = (message) => {
    showSnackbar(message, "success")
  }

  return (
    <Box>
      <Card sx={{ mb: 3 }}>
        <CardContent>
          {/* Action Buttons Section */}
          <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap" }}>
            <Button variant="contained" onClick={handleAddNationalSalesManagerClick}>
              Add National Sales Manager
            </Button>
            <Button variant="outlined" onClick={handleDownloadPdf} disabled={downloading}>
              {downloading ? <CircularProgress size={20} sx={{ mr: 1 }} /> : null}
              All National Sales Managers
            </Button>
          
          </Box>

          {/* Search and Entries Per Page Section */}
          <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6} md={4}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography variant="body2">Show</Typography>
                <FormControl size="small" sx={{ minWidth: 80 }}>
                  <Select
                    value={entriesPerPage}
                    onChange={handleEntriesChange}
                    displayEmpty
                    inputProps={{ "aria-label": "Entries per page" }}
                  >
                    <MenuItem value={10}>10</MenuItem>
                    <MenuItem value={25}>25</MenuItem>
                    <MenuItem value={50}>50</MenuItem>
                    <MenuItem value={100}>100</MenuItem>
                  </Select>
                </FormControl>
                <Typography variant="body2">entries</Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={8}>
              <TextField
                label="Search"
                variant="outlined"
                size="small"
                value={searchTerm}
                onChange={handleSearchChange}
                fullWidth
                sx={{ ml: { sm: "auto" } }}
              />
            </Grid>
          </Grid>

          {/* Table Section */}
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Alert severity="error">{error}</Alert>
          ) : (
            <TableContainer component={Paper} elevation={0} variant="outlined">
              <Box sx={{ overflowX: "auto", minWidth: 650 }}>
                <Table aria-label="national sales manager table">
                  <TableHead sx={{ bgcolor: "#f5f5f5" }}>
                    <TableRow>
                      <TableCell sx={{ fontWeight: "bold", width: "5%", borderRight: "1px solid #e0e0e0" }}>
                        Sl
                      </TableCell>
                      <TableCell sx={{ fontWeight: "bold", width: "25%", borderRight: "1px solid #e0e0e0" }}>
                        <Button onClick={() => requestSort("name")} sx={{ textTransform: "none", p: 0, minWidth: 0 }}>
                          Team Name {getSortIcon("name")}
                        </Button>
                      </TableCell>
                      <TableCell sx={{ fontWeight: "bold", width: "25%", borderRight: "1px solid #e0e0e0" }}>
                        <Button onClick={() => requestSort("group")} sx={{ textTransform: "none", p: 0, minWidth: 0 }}>
                          NSM Name  {getSortIcon("group")}
                        </Button>
                      </TableCell>
                      <TableCell sx={{ fontWeight: "bold", width: "15%", borderRight: "1px solid #e0e0e0" }}>
                        <Button
                          onClick={() => requestSort("mobile_no")}
                          sx={{ textTransform: "none", p: 0, minWidth: 0 }}
                        >
                          Mobile No {getSortIcon("mobile_no")}
                        </Button>
                      </TableCell>
                      <TableCell sx={{ fontWeight: "bold", width: "30%", borderRight: "1px solid #e0e0e0" }}>
                        <Button
                          onClick={() => requestSort("address")}
                          sx={{ textTransform: "none", p: 0, minWidth: 0 }}
                        >
                          Address {getSortIcon("address")}
                        </Button>
                      </TableCell>
                      <TableCell sx={{ fontWeight: "bold", width: "10%" }}>Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {displayedNationalSalesManagers.length > 0 ? (
                      displayedNationalSalesManagers.map((manager, index) => (
                        <TableRow key={manager.id || index}>
                          <TableCell sx={{ borderRight: "1px solid #e0e0e0" }}>
                            {indexOfFirstNationalSalesManager + index + 1}
                          </TableCell>
                          <TableCell sx={{ borderRight: "1px solid #e0e0e0" }}>{manager.nsm_team_name}</TableCell>
                          <TableCell sx={{ borderRight: "1px solid #e0e0e0" }}>{manager.ns_manager_name}</TableCell>
                          <TableCell sx={{ borderRight: "1px solid #e0e0e0" }}>{manager.mobile_no}</TableCell>
                          <TableCell sx={{ borderRight: "1px solid #e0e0e0" }}>{manager.address}</TableCell>
                          <TableCell>
                            <Box sx={{ display: "flex", gap: 1 }}>
                              <Button
                                variant="outlined"
                                size="small"
                                onClick={() => handleEditNationalSalesManagerClick(manager.id)}
                              >
                                <Edit fontSize="small" />
                              </Button>
                              <Button
                                variant="contained"
                                color="error"
                                size="small"
                                onClick={() => handleDeleteNationalSalesManagerClick(manager.id)}
                              >
                                <Delete fontSize="small" />
                              </Button>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={6} sx={{ textAlign: "center", py: 4 }}>
                          No National Sales Managers found.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </Box>
            </TableContainer>
          )}

          {/* Pagination Section */}
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              justifyContent: "space-between",
              alignItems: { xs: "center", sm: "center" },
              gap: 2,
              mt: 2,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              Showing {startEntry} to {endEntry} of {totalEntries} entries
            </Typography>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button variant="outlined" onClick={handlePreviousPage} disabled={currentPage === 1}>
                Prev
              </Button>
              {renderPageNumbers()}
              <Button variant="outlined" onClick={handleNextPage} disabled={currentPage === totalPages}>
                Next
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Add National Sales Manager Modal (currently using AddNsmModal) */}
      <AddNsmModal
        open={addModalOpen}
        onClose={handleAddModalClose}
        onSaveSuccess={handleNationalSalesManagerAddedSuccess}
      />

      {/* Edit National Sales Manager Modal (currently using EditNsmModal) */}
      {editModalOpen && (
        <EditNsmModal
          open={editModalOpen}
          onClose={handleEditModalClose}
          onUpdateSuccess={handleNationalSalesManagerUpdatedSuccess}
          nsmId={currentNationalSalesManagerId}
        />
      )}

      {/* Add National Sales Manager Group Modal (currently using AddNsmGroupModal) */}
      <AddNsmGroupModal
        open={addNationalSalesManagerGroupModalOpen}
        onClose={handleAddNationalSalesManagerGroupModalClose}
        onSaveSuccess={handleNationalSalesManagerGroupAddedSuccess}
      />

      {/* Confirmation Dialog for Delete */}
      <ConfirmationDialog
        open={confirmDialogOpen}
        onClose={handleConfirmDialogClose}
        onConfirm={handleConfirmNationalSalesManagerDelete}
        title="Are you sure?"
        message="Delete This Data?"
        confirmText="Yes, delete it!"
        cancelText="Cancel"
      />
    </Box>
  )
}

export default NationalSalesManager
